import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams, useNavigate, NavLink, Link } from "react-router-dom";
import { Header } from "./components/Header";
import { ShimmerTable } from "react-shimmer-effects";
import toast, { Toaster } from "react-hot-toast";
import { type } from "@testing-library/user-event/dist/type";

const API_URL = "/api";

export default function ScoreCard() {
  const { id } = useParams();
  console.log("🔎 Route id:", id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [company, setCompany] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checklistFormData, setChecklistFormData] = useState([]);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/getTransactionDetails/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        console.log("📥 Full API Response:", data);
        setTransactionData(data);
        console.log("transactionData:", transactionData);
        // setFormData(data?.DocumentCheckList || {});
        setFormData(data); // keep full object for Loan Info
        setCompany(data);
        console.log("Data:", data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, []);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setFormData(transactionData);
    setChecklistFormData(transactionData?.DocumentCheckList || []);
    setIsEditing(false);
  };

  const renderStatusCell = (section) => {
    let value = formData?.[section]?.STATUS || "";
    if (section === "ENTEREXPIRYDATE") value = "";
    return isEditing ? (
      <select
        className="form-select"
        value={value}
        onChange={(e) => handleChange(e, section, "STATUS")}
      >
        <option value="">Select</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
        <option value="N/A">N/A</option>
      </select>
    ) : (
      <div
        style={{
          backgroundColor: value === "Yes" ? "#58a65c" : "#fdf3d0",
          color: value === "Yes" ? "#fff" : "#000",
          textAlign: "center",
        }}
      >
        {value || ""}
      </div>
    );
  };

  const criteriaLabels = {
    COMPANYNAMEISCORRECT: "Company name is correct",
    THEREGISTRATIONISCURRENT: "The registration is current",
    THERETURNCOVERSTHEMOSTRECENTPERIOD:
      "The return covers the most recent period",
    SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED:
      "Shareholder(s) match organization chart (if provided)",
    THECHARTISSIGNEDANDDATED: "The chart is signed and dated",
    STATEMENTSARESIGNEDBYAUDITOR: "Statements are signed by auditor",
  };

  const renderCheckCell = (section) => {
    // if (section === "SUBMITTEDBUSINESSREGISTRATION") return "";

    // Show the label for these fields instead of CHECKINGDETAILS
    if (
      section === "COMPANYNAMEISCORRECT" ||
      section === "THEREGISTRATIONISCURRENT" ||
      section === "THERETURNCOVERSTHEMOSTRECENTPERIOD" ||
      section === "SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED" ||
      section === "THECHARTISSIGNEDANDDATED" ||
      section === "STATEMENTSARESIGNEDBYAUDITOR"
    ) {
      return criteriaLabels[section] || "";
    }

    if (section === "ENTEREXPIRYDATE") {
      const rawDate = formData?.ENTEREXPIRYDATE?.CHECKINGDETAILS;
      return rawDate ? formatDate(rawDate) : "";
    }

    return formData?.[section]?.CHECKINGDETAILS || "";
  };

  const handleInputChange = (sectionKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const { _id, ...rest } = formData;
    if (!_id) {
      alert("Missing _id");
      return;
    }
    // Remove any fields with undefined values
    const cleanedData = {};
    for (const key in rest) {
      if (rest[key] !== undefined) {
        cleanedData[key] = rest[key];
      }
    }
    const updatePayload = {
      _id: _id, // Make sure it's a string
      update_data: cleanedData,
    };

    try {
      const response = await fetch(
        "http://localhost:8000/riverchain/techop/v1.1/updateCompanyLog",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatePayload),
        }
      );

      const resultText = await response.text();

      if (!response.ok) {
        console.error("❌ Update failed:", resultText);
        alert("Update failed:\n" + resultText);
      } else {
        toast.success("Company log updated successfully");
      }
    } catch (error) {
      console.error("🚨 Network error:", error);
      toast.danger("An error occurred while updating the profile.");
    } finally {
      setIsSubmitting(false);
    }
  };
  // Utility: format date values into DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString; // fallback if invalid date
    return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
  };

  const indicatorConfig = {
    "Riverchain Score": {
      options: ["Borrower: A", "Borrower: B", "Borrower: C", "Borrower: D"],
      scores: {
        "Borrower: A": 20,
        "Borrower: B": 15,
        "Borrower: C": 0,
        "Borrower: D": -10,
      },
    },
    "Duration (Term)": {
      options: [
        "Less than 3 months (90 days)",
        "Between 3 months and 6 months",
        "Between 6 months and 12 months",
        "Longer than 12 months",
      ],
      scores: {
        "Less than 3 months (90 days)": 10,
        "Between 3 months and 6 months": 5,
        "Between 6 months and 12 months": 0,
        "Longer than 12 months": -10,
      },
    },
    "Work Complexity": {
      options: [
        "Standard construction works with normal level of complexity",
        "Complex projects that require specialized materials and skillsets",
      ],
      scores: {
        "Standard construction works with normal level of complexity": 5,
        "Complex projects that require specialized materials and skillsets": 0,
      },
    },
    "Involvement of an independent technical and environmental expert": {
      options: [
        "Third-party consultant and Quantity Surveyor",
        "No Third-party consultant and Quantity Surveyor",
      ],
      scores: {
        "Third-party consultant and Quantity Surveyor": 5,
        "No Third-party consultant and Quantity Surveyor": 0,
      },
    },
    "Supply risk of necessary raw materials and labour": {
      options: [
        "No know supply risks",
        "Know limited supply or price volatility of raw materials and labour",
      ],
      scores: {
        "No know supply risks": 0,
        "Know limited supply or price volatility of raw materials and labour":
          -5,
      },
    },
    "Evidence of project progress": {
      options: [
        "Construction is in progress",
        "Project has not been started yet",
      ],
      scores: {
        "Construction is in progress": 10,
        "Project has not been started yet": 0,
      },
    },
    Terms: {
      options: [
        "Standard contract terms (assignability, payment terms, surety bond, confidentiality)",
        "Non-standard contract terms (assignability, payment terms, surety bond, confidentiality)",
        "Unknow terms (i.e. no information supplied)",
      ],
      scores: {
        "Standard contract terms (assignability, payment terms, surety bond, confidentiality)": 0,
        "Non-standard contract terms (assignability, payment terms, surety bond, confidentiality)":
          -5,
        "Unknow terms (i.e. no information supplied)": -5,
      },
    },
    "Contract history of delays and accidents": {
      options: [
        "No known history of delays and accidents on the job/contract",
        "Known history of  delays and accidents on the job/contract",
      ],
      scores: {
        "No known history of delays and accidents on the job/contract": 0,
        "Known history of  delays and accidents on the job/contract": -10,
      },
    },
    "Relationship with Riverchain": {
      options: [
        "Existing relationship with Riverchain",
        "No prior relationship with Riverchain",
      ],
      scores: {
        "Existing relationship with Riverchain": 5,
        "No prior relationship with Riverchain": 0,
      },
    },
    Reputation: {
      options: [
        "Positive market reputation",
        "Neutral market reputation",
        "Negative market reputation",
      ],
      scores: {
        "Positive market reputation": 5,
        "Neutral market reputation": 0,
        "Negative market reputation": -5,
      },
    },
    "Financial rating / Credit rating (from Third-party Credit Rating Agency)":
      {
        options: ["A and B", "C", "Below C", "N/A"],
        scores: { "A and B": 5, C: 0, "Below C": -5, "N/A": 0 },
      },
  };

  // const scoreMappingTransaction = {
  //   "0% to 10%": 0,
  //   "11% to 20%": 5,
  //   "21% to 30%": 8,
  //   "> 30%": 10,
  // };
  // Dropdown options + scores for each Indicator
  const indicatorConfigTransaction = {
    "Effect of income alteration on security": {
      options: ["0% to 10%", "11% to 20%", "21% to 30%", "> 30%"],
      scores: { "0% to 10%": 0, "11% to 20%": 5, "21% to 30%": 8, "> 30%": 10 },
    },
    "Lifetime of the Project / Term of the special loan": {
      options: ["< 1.0", "1.0 to 1.1", "1.2 to 1.5", "> 1.5"],
      scores: { "< 1.0": -10, "1.0 to 1.1": 0, "1.2 to 1.5": 5, "> 1.5": 10 },
    },
    "Collection account ": {
      options: [
        "Borrower will use Riverchain collection account",
        "Borrower will not use Riverchain collection account ",
      ],
      scores: {
        "Borrower will use Riverchain collection account": 10,
        "Borrower will not use Riverchain collection account ": -5,
      },
    },
    "Type of Security ": {
      options: [
        "Unsecured",
        "Receivables",
        "Receivables and Other Assets or  Guarantees",
      ],
      scores: {
        Unsecured: -10,
        Receivables: 0,
        "Receivables and Other Assets or  Guarantees": 10,
      },
    },
    "Months of low Loan/NEP/term": {
      options: ["0", "1 to 3", "> 3"],
      scores: { 0: 10, "1 to 3": 0, "> 3": -10 },
    },
    "Exposure to Borrower": {
      options: [
        "Exposure to Borrower is < 10% of Portfolio",
        "Exposure to Borrower is > 10% of Portfolio",
      ],
      scores: {
        "Exposure to Borrower is < 10% of Portfolio": 0,
        "Exposure to Borrower is > 10% of Portfolio": -10,
      },
    },
    "Exposure to Project ": {
      options: [
        "Exposure to Project is < 10% of Portfolio",
        "Exposure to Project is > 10% of Portfolio",
      ],
      scores: {
        "Exposure to Project is < 10% of Portfolio": 0,
        "Exposure to Project is > 10% of Portfolio": -10,
      },
    },
    "Exposure to Counterparty ": {
      options: [
        "Exposure to the Counterparty is < 25% of Portfolio",
        "Exposure to the Counterparty is > 25% of Portfolio",
      ],
      scores: {
        "Exposure to the Counterparty is < 25% of Portfolio": 0,
        "Exposure to the Counterparty is > 25% of Portfolio": -5,
      },
    },
    "Exposure to Employer": {
      options: [
        "Exposure to the Employer is < 50% of Portfolio",
        "Exposure to the Employer is > 50% of Portfolio",
      ],
      scores: {
        "Exposure to the Employer is < 50% of Portfolio": 0,
        "Exposure to the Employer is > 50% of Portfolio": -5,
      },
    },
  };

  return (
    <div className="app-container">
      <Toaster />
      <div className="container-fluid">
        <main className="content">
          <Header />
        </main>
      </div>

      <div className="container">
        <div className="row mt-4">
          <div className="col-md-12">
            <NavLink to={`/Dashboard`} className="btn btn-dark ms-2">
              Back to Transaction List
            </NavLink>

            <br />
            <br />
            <NavLink
              to={`/ViewTransactionList/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Loan Details
            </NavLink>

            <NavLink
              to={`/Drawdowns/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Drawdowns
            </NavLink>

            <NavLink
              to={`/ApplicationChecklist/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Application Checklist
            </NavLink>

            <NavLink to={`/ScoreCard/${id}`} className="btn btn-dark ms-2">
              Scorecard
            </NavLink>

            <NavLink
              to={`/TransactionSummary/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Transaction Summary
            </NavLink>

            <NavLink
              to={`/TermSheet/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Term Sheet
            </NavLink>

            <NavLink
              to={`/Payments/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Payments
            </NavLink>

            <NavLink
              to={`/InterestCalculations/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Interest Calculations
            </NavLink>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 style={{ marginTop: "15px" }}>Score Card</h3>
              <div>
                {isEditing && (
                  <button
                    className="btn btn-secondary me-2 btn-sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={async () => {
                    if (isEditing) {
                      setIsSubmitting(true);
                      await handleSubmit();
                      setIsSubmitting(false);
                    } else {
                      setFormData(transactionData);
                      setIsEditing(true);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {isEditing
                    ? isSubmitting
                      ? "Submitting..."
                      : "Submit"
                    : "Edit"}
                </button>
              </div>
            </div>

            {loading || !transactionData ? (
              <ShimmerTable row={5} col={5} />
            ) : (
              <>
                <div className="col-md-12">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <td>
                          <h4>
                            <strong>Borrower:</strong>
                          </h4>
                        </td>
                        <td>
                          <h4>{transactionData?.BORROWER || "N/A"}</h4>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <h4>
                            <strong>Transaction ID:</strong>
                          </h4>
                        </td>
                        <td>
                          <h4>{transactionData?.TRANSACTIONID || "N/A"}</h4>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Business Risk Table */}

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        ></th>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        >
                          Indicator
                        </th>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        >
                          Input
                        </th>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        >
                          Score
                        </th>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        >
                          Comments
                        </th>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        >
                          Reviewer Check
                        </th>
                      </tr>
                      <tr>
                        <th
                          colSpan="6"
                          style={{
                            backgroundColor: "#a5cfcfff",
                            textAlign: "left",
                          }}
                        >
                          Business Risk
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(transactionData?.Business_Risk) &&
                      transactionData.Business_Risk.length > 0 ? (
                        <>
                          {/* ✅ Filter out "Business Risk Score" before mapping */}
                          {transactionData.Business_Risk.filter(
                            (item) => item.Criteria !== "Business Risk Score"
                          ).map((item, index) => (
                            <tr key={index}>
                              {/* Criteria */}
                              <td style={{ fontWeight: "bold" }}>
                                {item.Criteria || ""}
                              </td>

                              {/* Indicator */}
                              <td>{item.Indicator || ""}</td>

                              {/* Input */}
                              <td>
                                {isEditing
                                  ? (() => {
                                      // find matching config by Indicator (trim + case-insensitive)
                                      const indicatorKey = Object.keys(
                                        indicatorConfig
                                      ).find(
                                        (key) =>
                                          key.trim().toLowerCase() ===
                                          (item.Indicator?.trim().toLowerCase() ||
                                            "")
                                      );
                                      const config =
                                        indicatorConfig[indicatorKey];

                                      return config ? (
                                        <select
                                          className="form-select"
                                          value={item.Input || ""}
                                          onChange={(e) => {
                                            const selectedInput =
                                              e.target.value;
                                            const newData = [
                                              ...transactionData.Business_Risk,
                                            ];
                                            newData[index] = {
                                              ...item,
                                              Input: selectedInput,
                                              Score:
                                                config.scores[selectedInput] !==
                                                undefined
                                                  ? config.scores[selectedInput]
                                                  : null,
                                            };
                                            setTransactionData((prev) => ({
                                              ...prev,
                                              Business_Risk: newData,
                                            }));
                                          }}
                                        >
                                          <option value="">-- Select --</option>
                                          {config.options.map((opt, i) => (
                                            <option key={i} value={opt}>
                                              {opt}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <span style={{ color: "gray" }}>
                                          No dropdown
                                        </span>
                                      );
                                    })()
                                  : item.Input || ""}
                              </td>

                              {/* Score */}
                              <td>{item.Score ?? ""}</td>

                              {/* Comments */}
                              <td>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={item.Comments || ""}
                                    onChange={(e) => {
                                      const newData = [
                                        ...transactionData.Business_Risk,
                                      ];
                                      newData[index] = {
                                        ...item,
                                        Comments: e.target.value,
                                      };
                                      setTransactionData((prev) => ({
                                        ...prev,
                                        Business_Risk: newData,
                                      }));
                                    }}
                                  />
                                ) : (
                                  item.Comments || ""
                                )}
                              </td>

                              {/* Reviewer Check */}
                              <td>
                                {isEditing ? (
                                  <select
                                    className="form-select"
                                    value={item.ReviewerCheck || "N/A"}
                                    onChange={(e) => {
                                      const newData = [
                                        ...transactionData.Business_Risk,
                                      ];
                                      newData[index] = {
                                        ...item,
                                        ReviewerCheck: e.target.value,
                                      };
                                      setTransactionData((prev) => ({
                                        ...prev,
                                        Business_Risk: newData,
                                      }));
                                    }}
                                  >
                                    <option value="N/A">N/A</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                  </select>
                                ) : (
                                  item.ReviewerCheck || "N/A"
                                )}
                              </td>
                            </tr>
                          ))}

                          {/* ✅ Total Score Row (exclude Business Risk Score from sum too) */}
                          <tr
                            style={{
                              fontWeight: "bold",
                              backgroundColor: "#f8f8f8",
                            }}
                          >
                            <td colSpan="3" style={{ textAlign: "right" }}>
                              Business Risk Score:
                            </td>
                            <td>
                              {transactionData.Business_Risk.filter(
                                (item) =>
                                  item.Criteria !== "Business Risk Score"
                              ).reduce(
                                (sum, item) =>
                                  sum + (item.Score ? Number(item.Score) : 0),
                                0
                              )}
                            </td>
                            <td colSpan="3"></td>
                          </tr>
                        </>
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ textAlign: "center" }}>
                            No Business Risk Data Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        ></th>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        >
                          Indicator
                        </th>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        >
                          Input
                        </th>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        >
                          Score
                        </th>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        >
                          Comments
                        </th>
                        <th
                          style={{
                            backgroundColor: "#173660",
                            color: "#fff",
                            textAlign: "center",
                          }}
                        >
                          Reviewer Check
                        </th>
                      </tr>
                      <tr>
                        <th
                          colSpan="6"
                          style={{
                            backgroundColor: "#a5cfcfff",
                            textAlign: "left",
                          }}
                        >
                          Transaction Risk
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(transactionData?.Transaction_Risk) &&
                      transactionData.Transaction_Risk.length > 0 ? (
                        <>
                          {transactionData.Transaction_Risk.map(
                            (item, index) => (
                              <tr key={index}>
                                <td style={{ fontWeight: "bold" }}>
                                  {item.Criteria || ""}
                                </td>
                                {/* Indicator */}
                                <td>{item.Indicator || ""}</td>

                                {/* Input */}
                                <td>
                                  {isEditing
                                    ? (() => {
                                        const config =
                                          indicatorConfigTransaction[
                                            item.Indicator
                                          ];
                                        if (!config) return item.Input || "";

                                        return (
                                          <select
                                            className="form-select"
                                            value={item.Input || ""}
                                            onChange={(e) => {
                                              const selectedInput =
                                                e.target.value;
                                              const score =
                                                config.scores[selectedInput] ??
                                                0;
                                              const newData = [
                                                ...transactionData.Transaction_Risk,
                                              ];
                                              newData[index] = {
                                                ...item,
                                                Input: selectedInput,
                                                Score: score,
                                              };

                                              setTransactionData((prev) => ({
                                                ...prev,
                                                Transaction_Risk: newData,
                                              }));
                                            }}
                                          >
                                            <option value="">
                                              -- Select --
                                            </option>
                                            {config.options.map((opt, i) => (
                                              <option key={i} value={opt}>
                                                {opt}
                                              </option>
                                            ))}
                                          </select>
                                        );
                                      })()
                                    : item.Input || ""}
                                </td>
                                {/* Score */}
                                <td>{item.Score ?? ""}</td>

                                {/* Comments */}
                                <td>
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={item.Comments || ""}
                                      onChange={(e) => {
                                        const newData = [
                                          ...transactionData.Transaction_Risk,
                                        ];
                                        newData[index] = {
                                          ...item,
                                          Comments: e.target.value,
                                        };
                                        setTransactionData((prev) => ({
                                          ...prev,
                                          Transaction_Risk: newData,
                                        }));
                                      }}
                                    />
                                  ) : (
                                    item.Comments || ""
                                  )}
                                </td>

                                {/* Reviewer Check */}
                                <td>
                                  {isEditing ? (
                                    <select
                                      className="form-select"
                                      value={item.ReviewerCheck || "N/A"}
                                      onChange={(e) => {
                                        const newData = [
                                          ...transactionData.Transaction_Risk,
                                        ];
                                        newData[index] = {
                                          ...item,
                                          ReviewerCheck: e.target.value,
                                        };
                                        setTransactionData((prev) => ({
                                          ...prev,
                                          Transaction_Risk: newData,
                                        }));
                                      }}
                                    >
                                      <option value="N/A">N/A</option>
                                      <option value="Yes">Yes</option>
                                      <option value="No">No</option>
                                    </select>
                                  ) : (
                                    item.ReviewerCheck || "N/A"
                                  )}
                                </td>
                              </tr>
                            )
                          )}

                          {/* ✅ Total Score row */}
                          <tr
                            style={{
                              fontWeight: "bold",
                              backgroundColor: "#f8f8f8",
                            }}
                          >
                            <td colSpan="3" style={{ textAlign: "right" }}>
                              Transaction Risk Score:
                            </td>
                            <td>
                              {(() => {
                                const totalScore =
                                  transactionData.Transaction_Risk.reduce(
                                    (sum, item) =>
                                      sum + (Number(item.Score) || 0),
                                    0
                                  );
                                return totalScore;
                              })()}
                            </td>
                            <td colSpan="2"></td>
                          </tr>
                        </>
                      ) : (
                        <tr>
                          <td colSpan="5" style={{ textAlign: "center" }}>
                            No Transaction Risk Score Data Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <table className="table table-bordered">
                    <tbody>
                      {(() => {
                        const totalScore =
                          transactionData.Transaction_Risk.reduce(
                            (sum, item) => sum + (Number(item.Score) || 0),
                            0
                          );
                        const isLow = totalScore <= 0;
                        const ratingText = isLow ? "CIV" : "CII";
                        const bgColor = isLow ? "#cb2b0f" : "#fffacd"; // red / light yellow

                        return (
                          <tr>
                            <td style={{ textAlign: "center" }}>
                              <strong>Transaction Rating</strong>
                            </td>
                            <td
                              style={{
                                backgroundColor: bgColor,
                                color: isLow ? "#fcfaf9" : "#000",
                                textAlign: "center",
                                fontWeight: "bold",
                              }}
                            >
                              {ratingText}
                            </td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
